import { useCallback, useEffect, useState } from 'react'
import { ApiError, bookingApi, type MyBooking } from '@/api/client'
import { fullDateTime, timeLabel } from '@/lib/format'

const STATUS_LABEL: Record<string, string> = {
  pending: 'Ожидает',
  confirmed: 'Подтверждена',
  active: 'Идёт'
}

function errMsg(e: unknown): string {
  return e instanceof ApiError ? e.message : 'Не удалось загрузить брони.'
}

function tableLabel(b: MyBooking): string {
  if (b.tableNumber != null) return `Стол ${b.tableNumber}`
  return b.tableName ?? 'Стол —'
}

export function MyBookingsPage({
  slug,
  onBook
}: {
  slug: string
  onBook?: () => void
}): React.JSX.Element {
  const [tz, setTz] = useState<string | null>(null)
  // Код типа игры → отображаемое имя (из справочника клуба).
  const [kindNames, setKindNames] = useState<Record<string, string>>({})
  const [list, setList] = useState<MyBooking[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [info, rows] = await Promise.all([
        bookingApi.clubInfo(slug).catch(() => null),
        bookingApi.tmaMyBookings(slug)
      ])
      setTz(info?.timezone ?? null)
      setKindNames(Object.fromEntries((info?.gameTypes ?? []).map((g) => [g.code, g.name])))
      setList(rows)
    } catch (e) {
      setError(errMsg(e))
      setList([])
    }
  }, [slug])

  useEffect(() => {
    void load()
  }, [load])

  const cancel = async (id: string) => {
    setBusyId(id)
    setError(null)
    try {
      await bookingApi.tmaCancelBooking(slug, id)
      setConfirmId(null)
      await load()
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="page">
      <div className="shell">
        <div className="brand">
          <div className="logo">
            8<b>Ball</b>
          </div>
          <div className="sub">Мои брони</div>
        </div>

        {error && (
          <div className="card">
            <div className="card-body">
              <div className="error">{error}</div>
            </div>
          </div>
        )}

        {list === null && !error && (
          <div className="center">
            <div className="spinner" />
          </div>
        )}

        {list !== null && list.length === 0 && (
          <div className="card">
            <div className="card-body">
              <p className="empty">У вас пока нет предстоящих броней.</p>
              {onBook && (
                <div className="actions">
                  <button className="btn btn-primary" onClick={onBook}>
                    Забронировать стол
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {list !== null && list.length > 0 && (
          <div className="mb-list">
            {list.map((b) => (
              <div className="mb-card" key={b.id}>
                <div className="mb-head">
                  <div className="mb-when">{fullDateTime(b.startsAt, tz)}</div>
                  <span className={`mb-status ${b.status}`}>
                    {STATUS_LABEL[b.status] ?? b.status}
                  </span>
                </div>
                <div className="mb-meta">
                  {tableLabel(b)} · {timeLabel(b.startsAt, tz)}–{timeLabel(b.endsAt, tz)}
                  {b.gameKind ? ` · ${kindNames[b.gameKind] ?? b.gameKind}` : ''}
                </div>

                {confirmId === b.id ? (
                  <div className="mb-confirm">
                    <span>Отменить бронь?</span>
                    <button
                      className="btn btn-danger"
                      disabled={busyId === b.id}
                      onClick={() => cancel(b.id)}
                    >
                      {busyId === b.id ? 'Отменяем…' : 'Да, отменить'}
                    </button>
                    <button className="btn btn-ghost" onClick={() => setConfirmId(null)}>
                      Нет
                    </button>
                  </div>
                ) : (
                  <div className="mb-actions">
                    <button className="linkbtn danger" onClick={() => setConfirmId(b.id)}>
                      Отменить бронь
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
